import { ApplyInputResultPool } from "game-common/apply_input_result_pool"
import { WeaponAmmo } from "game-common/character/character"
import { EntityManager } from "game-common/entity_manager"
import {
    ItemTypeMeta,
    locateInventoryItemObjectByItemType,
    locateInventoryReadiedItemObjects,
} from "game-common/item/item"
import { TileEffect } from "game-common/map/map"
import { InteractionType } from "game-common/mechanics/entity_mechanics"
import { Mechanics } from "game-common/mechanics/mechanics"
import { DayPhase } from "game-common/mechanics/physics_mechanics"
import {
    AbilityType,
    Appearance,
    ApplyInputResult,
    BuildingPointOptions,
    Coordinate,
    EmberRosterEntry,
    EmberRosterRole,
    Entity,
    EntityDimensions,
    EntityId,
    EntityTypes,
    FactionTrackingConfig,
    RoomAmbience,
    Latch,
    Message,
    Orientation,
    RunLevel,
    TileSize,
    Visibility,
    Weapon,
    WeaponCharacteristics,
    WorldState,
    collisionMover,
    getDistance,
    getEntityDistance,
} from "game-common/models"
import { emitWeaponParticles } from "game-common/weapons"
import { v4 as uuidv4 } from "uuid"

import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { Callback, emptyCoordinate, resolveQuadrantIdFrom, toCoordinateFromSimple } from "game-common/util"
import { Audio } from "./audio"
import { Client } from "./client"
import { ClientRenderer, RenderingFactory } from "./client_models"
import { Player } from "./player"
import { ScalingParams } from "./client_util"
import { Faction } from "game-common/social/faction"
import { ClientViewportManager } from "./rendering/impl/pixijs/client_viewport_manager"
import { RoomReveal } from "./room_reveal"

export type EntityInteractionCallback = (entityId: EntityId, interaction: InteractionType) => void

export const PlayerEntityId: { value: string } = {
    value: "",
}

export class ClientGameLogic {
    private destroyedEntities: Set<EntityId> = new Set<EntityId>()
    private destroyedEntitiesLagBuffer: Set<EntityId> = new Set<EntityId>()

    // Viewport bounds caching
    private cachedViewportBounds: { minX: number; maxX: number; minY: number; maxY: number } = {
        minX: 0,
        maxX: 0,
        minY: 0,
        maxY: 0,
    }
    private lastPlayerPosForViewport: Coordinate
    private viewportBoundsValid: boolean = false
    private readonly VIEWPORT_CACHE_THRESHOLD = 16 // Only recalculate if player moves more than 16 pixels

    private expiredTs: number = 0
    private lagbufferTs: Latch = new Latch(1500)
    private lastFireTs: Latch
    private lastWeapon: Weapon

    tilemapMetaProvider: TileMapMetaProvider

    roomId?: EntityId
    quadrantId?: string = ""
    playerController: Player

    playerEntity: Entity
    entityManager: EntityManager<Entity> = new EntityManager<Entity>(EntityTypes)

    clientRenderer: ClientRenderer
    audio: Audio
    dayPhase: DayPhase
    currentBrightness: number = 0.0
    runLevel: RunLevel

    started: boolean

    // Faction tracking: offscreen edge arrows pointing to members of a faction. Both are set by the
    // server (per-room) on room entry and are not the player's to turn off -- in the one room type
    // that asks for them, an ember camp, they point at the player's own camp and crew, and there is
    // no moment where losing track of those is an improvement. (`f` used to toggle them; it is the
    // frame-rate readout now.)
    trackedFaction: Faction | null = null
    trackingEnabled: boolean = false

    // This room's atmosphere -- leaves, fog, butterflies (see RoomAmbience). Configured per room by
    // the server and sent on every room entry, empty included, so a camp's weather cannot follow the
    // player out. Read by the renderer's effects each frame; null before the first room entry, which
    // they treat as nothing at all.
    ambience: RoomAmbience | null = null

    // Recent speech per entity, so the faction tracker can show a chat bubble on the
    // arrow when a tracked member speaks while offscreen (its own world-space bubble
    // would be off-screen). Expires like the avatar HUD bubble.
    private recentSpeech: Map<EntityId, { text: string; expiresAt: number }> = new Map()
    // Last line each entity said, kept a fixed ~10s (longer than recentSpeech's ~3s window), for the
    // follow-cam PiP caption so a distant roster member's last words linger there a while.
    private lastSpeech: Map<EntityId, { text: string; expiresAt: number }> = new Map()

    // Ember roster role per entity, mirrored from the roster broadcast so the offscreen
    // faction tracker can show each member's role glyph (👑/🛡️/🪓). The roster itself is
    // routed to the HUD widget; here we keep only the entityId -> role lookup.
    private emberRoles: Map<EntityId, EmberRosterRole> = new Map()

    // Follow-cam: the entity the picture-in-picture view is watching (set by the ember roster
    // click). While set, its neighborhood is kept "near" for rendering/interpolation so the PiP
    // shows it live, wherever it is in the room.
    focusEntityId: EntityId | null = null
    // Radius (px) around the focused entity kept live for the PiP. Slightly larger than the PiP
    // viewport so entities entering frame are already interpolating. Tune with the PiP size.
    private static FOCUS_RENDER_RADIUS = 14 * TileSize

    private incompleteEntities: Set<EntityId> = new Set()
    private brightnessInterval: NodeJS.Timeout
    private blackedOutTimeout: any

    // The curtain over a room that has landed but not finished arriving. See RoomReveal.
    private roomReveal: RoomReveal = new RoomReveal()

    // Unified culling system - single source of truth for entity distance culling
    private readonly CULLING_DISTANCE_TILES = 3
    private readonly cullingDistanceSquared =
        this.CULLING_DISTANCE_TILES * TileSize * (this.CULLING_DISTANCE_TILES * TileSize)

    // Entity sorting cache invalidation tracking
    private entitySortCacheInvalidated: boolean = false

    // Spatial indexing for efficient culling
    private viewportManager: ClientViewportManager | null = null

    constructor(factory: RenderingFactory) {
        this.clientRenderer = factory.create(this)
        this.audio = new Audio(this)
    }

    hasEntity = (entityId: EntityId): boolean => !!this.entityManager.get(entityId)

    getEntity = (entityId: EntityId): Entity | undefined => this.entityManager.get(entityId)

    updateScores = (scores: Record<EntityId, number>) => {
        const scoreEntityIds = Object.keys(scores)
        scoreEntityIds.forEach(entityId => {
            const entity = this.getEntity(entityId)
            if (entity) {
                this.clientRenderer.entity().update(entity, scores[entityId])
            }
        })
    }

    /**
     * Empty the world out before a reconnect hands us a new one.
     *
     * A new socket is a NEW session: the server destroys the player on close, so a reconnect comes
     * back with a different entityId and (for an ember player) a different room. Everything drawn
     * right now belongs to the session that just ended.
     *
     * initializePlayer cannot do this itself -- it only overwrites the `playerEntity` POINTER,
     * leaving the previous avatar in the entity manager and on the stage -- and neither can the
     * room-change teardown in updateFromWorldState, which deliberately spares whatever
     * `playerEntity` points at so a player survives walking between rooms. So the old player has to
     * go from here, explicitly, before anybody takes their place.
     *
     * `roomId` is cleared too, so the first world state of the new session reads as a room change
     * and rebuilds the tilemap rather than being taken for more of the room we just left.
     */
    prepareForReconnect = () => {
        const toRemove: EntityId[] = []
        this.entityManager.iterate(null, next => {
            this.clientRenderer.entity().destroy(next)
            toRemove.push(next.entityId)
        })
        toRemove.forEach(entityId => this.entityManager.remove(entityId))
        this.playerEntity = undefined
        this.roomId = undefined
        this.incompleteEntities.clear()
        this.destroyedEntities.clear()
        this.destroyedEntitiesLagBuffer.clear()
        this.viewportManager?.reset()
        this.invalidateViewportCache()
        this.invalidateEntitySortCache()
    }

    initializePlayer = (entityId: EntityId, client: Client, guest: boolean) => {
        PlayerEntityId.value = entityId

        const playerEntity = new Entity()
        playerEntity.entityType = "player"
        playerEntity.entityId = entityId
        playerEntity.dimensions = { ...EntityDimensions["player"] }
        playerEntity.hp = -1
        playerEntity.mana = -1
        playerEntity.hunger = -1
        playerEntity.visible = false
        this.playerEntity = playerEntity
        this.entityManager.set(this.playerEntity)

        // A reconnect comes back through here with the old controller still listening to the
        // keyboard and mouse. Letting it go first is what stops its listeners doubling up with the
        // new one's.
        this.playerController?.destroy()
        this.playerController = new Player(
            client,
            playerEntity,
            this.clientRenderer.mouse(),
            this.clientRenderer.keyboard(),
            guest,
        )

        this.clientRenderer.entity().create(playerEntity)
        this.clientRenderer.gui().helpMessaging()
    }

    updatePlayerEntityId = (entityId: EntityId) => {
        if (!this.playerEntity) {
            return
        }

        const originalEntityId = this.playerEntity.entityId
        this.playerEntity.entityId = entityId
        this.clientRenderer.entity().updateEntityId(originalEntityId, entityId)
    }

    shouldInterpolate = (entity: Entity) => {
        // No point in interpolating this client"s entity.
        if (entity.entityId == this.playerEntity?.entityId) {
            return false
        }

        // its a local bullet; don"t interpolate
        if (this.entityManager.contains(entity.entityId, "bullet")) {
            return false
        }

        return true
    }

    createEntity = (message: Message): Entity | undefined => {
        const { entityId, entityType } = message
        if (this.destroyedEntitiesLagBuffer.has(entityId) || message.entityState === "dead") {
            return
        }

        if (entityId === this.playerEntity?.entityId) {
            return this.playerEntity
        }

        // Keeps a room that is still arriving behind the curtain: the first of a load's entities
        // must not be mistaken for the last. No-op unless a room reveal is being held.
        this.roomReveal.noteArrival(Date.now())

        // create a local client entity based on the remote message from the server
        const entity = new Entity()

        entity.entityType = entityType
        entity.npcType = message.npcType
        entity.contextMenuSubject = message.contextMenuSubject
        entity.campfireMenuLearned = message.campfireMenuLearned
        entity.recruitMenuLearned = message.recruitMenuLearned
        entity.spriteId = message.spriteId
        entity.entityId = entityId
        entity.position_buffer = []
        if (message.origin) {
            entity.origin = { ...message.origin }
        }
        entity.maxDistance = message.maxDistance
        entity.location = { x: message?.position[0], y: message?.position[1] }
        entity.movement.direction = message.direction
        entity.movement.angle = message.angle
        entity.speed = message.speed
        entity.speed2 = message.speed2
        entity.color = message.color
        entity.dimensions = message.dimensions || { ...EntityDimensions[entityType] }
        entity.hp = -1
        entity.roomId = message.entityId
        entity.clickable = message.clickable
        entity.inventory = message.inventory
        entity.expirationTs = message.expirationTs
        entity.debug = message.debug
        entity.active = message.active
        entity.interestState = message.interest === "update" ? "dormant" : "active"

        // abort entity creation if its already travelled the maximum distance; should only
        // in theory apply to bullets
        if (entity.maxDistance !== undefined && entity.travelledDistance() > entity.maxDistance) {
            return
        }
        this.entityManager.set(entity)

        // Check if the new entity is within viewport - if so, invalidate cache
        if (!this.isTooDistantFromPlayer(entity)) {
            this.entitySortCacheInvalidated = true
        }

        //// for any created entity -- create a renderable representation
        //// and add it onto the displayable surface

        this.clientRenderer.entity().create(entity)

        return entity
    }

    canFireBulletFrom = (source: Entity): boolean => {
        if (!this.playerEntity.visible) {
            // can"t fire bullets when you are visible
            // todo - serverside enforcement
            return false
        }
        if (!source.canShootBullets) {
            return false
        }

        const { weapon } = source

        const weaponType = weapon?.weaponType || "none"
        const characteristics = WeaponCharacteristics[weaponType]
        if (!characteristics) {
            return false
        }

        if (source.mana < characteristics.manaCost) {
            return false
        }

        if (this.lastWeapon?.weaponType !== weaponType) {
            this.lastFireTs = new Latch(characteristics.firingLatchMs)
            this.lastWeapon = weapon
        }

        if (!this.lastFireTs.expired()) {
            return false
        }

        const ammoNeeded = WeaponAmmo[weaponType]
        if (ammoNeeded) {
            const foundAmmo = locateInventoryItemObjectByItemType(source.inventory, ammoNeeded)
            if (!foundAmmo || foundAmmo.quantity < 1) {
                this.handleChat("I need craft or buy more " + ammoNeeded + "s.", source.entityId, "")

                let foundSlot
                locateInventoryReadiedItemObjects(source.inventory).find((item, slot) => {
                    if (!WeaponAmmo[item.itemType]) {
                        foundSlot = slot
                        return item
                    }
                })

                if (foundSlot !== undefined) {
                    this.clientRenderer.gui().activateInventorySlot(foundSlot + 1)
                }

                return false
            }
        }

        return true
    }

    createBulletFrom = (source: Entity): Entity[] | undefined => {
        const bullets: Entity[] = emitWeaponParticles(
            this.lastWeapon,
            {
                ...source.location,
                roomId: source.roomId,
            },
            source.movement,
        )

        return bullets
    }

    createBombFrom = (source: Entity): Entity | undefined => {
        if (!this.playerEntity.visible) {
            // can"t fire bullets when you are visible
            // todo - serverside enforcement
            return
        }
        if (!source.canLayBomb) {
            return
        }
        if (source.mana < 3) {
            return
        }

        if (!source.inventory?.items.find(item => item.itemType === "bomb")) {
            return
        }

        const bomb = new Entity()
        bomb.entityType = "bomb"
        bomb.location = { ...source.location }
        bomb.entityId = uuidv4()
        bomb.dimensions = { ...EntityDimensions[bomb.entityType] }
        bomb.speed = 600
        bomb.roomId = source.roomId
        this.entityManager.set(bomb)

        ////

        this.clientRenderer.entity().create(bomb)

        return bomb
    }

    isBootstrapped = () => {
        return this.roomId && this.tilemapMetaProvider
    }

    updateFromWorldState = (worldState: WorldState) => {
        const {
            scores,
            tilemaps,
            worldMetaDimensions,
            tilemapMeta,
            visibility,
            roomId,
            roomName,
            leaderboards,
            dayPhase,
            dayPct,
            biomeColorMap,
            locationIcons,
            playerLocations,
            minimapConfig,
            runLevel,
        } = worldState

        // A full map snapshot replaces the scene even when the room ID is unchanged
        // (e.g. resetting an Ember camp). Ignoring it leaves the old map/entities in place
        // and never arms the reveal that completes the server's teleport fade-out.
        const updatedRoom = this.roomId !== roomId || !!(tilemaps && tilemapMeta)
        let updatedQuadrant = false
        if (updatedRoom) {
            //
            // we"re in a new room
            //
            this.runLevel = runLevel
            this.dayPhase = null
            this.roomId = roomId

            // Reset viewport manager for new room (more efficient than destroying/recreating)
            if (this.viewportManager) {
                this.viewportManager.reset()
            }

            // Clear viewport cache when room changes since map dimensions may change
            this.invalidateViewportCache()

            const currentQuardrantId = resolveQuadrantIdFrom(roomId, worldMetaDimensions)
            if (this.quadrantId !== currentQuardrantId) {
                this.quadrantId = currentQuardrantId
                updatedQuadrant = true
            }
            const toRemove = []
            this.entityManager.iterate(null, next => {
                if (next.entityId !== this.playerEntity.entityId) {
                    this.clientRenderer.entity().destroy(next)
                    toRemove.push(next.entityId)
                }
            })
            for (let i = 0; i < toRemove.length; i++) {
                this.entityManager.remove(toRemove[i])
            }
            this.incompleteEntities.clear()

            this.clientRenderer
                .gui()
                .roomUpdated(
                    roomId,
                    roomName,
                    visibility?.suppressMinimap,
                    visibility?.suppressDayCycle,
                    visibility?.suppressControlBar,
                    visibility?.suppressLevelUp,
                    visibility?.suppressMana,
                    visibility?.suppressFood,
                    visibility?.suppressShards,
                    visibility?.suppressPlayerName,
                    visibility?.suppressTutorial,
                    visibility?.suppressGuestPrompt,
                )
            this.clientRenderer.gui().updateLeaderboard(null)

            clearInterval(this.brightnessInterval)
            this.currentBrightness = dayPhase ? dayPhase.brightness : 0.0

            if (dayPct !== undefined) {
                this.clientRenderer.gui().updateDayPct(dayPct)
            }
            this.updateDayPhase(dayPhase)
            this.playerController.updateRoom()
        }

        if (scores) {
            this.updateScores(scores)
        }

        if (updatedRoom && tilemaps && tilemapMeta) {
            this.tilemapMetaProvider = new TileMapMetaProvider(roomId, tilemapMeta, tilemaps, worldMetaDimensions)
            this.tilemapMetaProvider.biomeColorMap = biomeColorMap
            this.tilemapMetaProvider.locationIcons = locationIcons
            this.tilemapMetaProvider.minimapConfig = minimapConfig
            this.entitySortCacheInvalidated = true

            // Initialize spatial indexing for efficient culling
            this.initializeViewportManager()

            this.clientRenderer.map().update(
                this.tilemapMetaProvider,
                visibility
                    ? visibility
                    : dayPhase
                    ? {
                          darkness: dayPhase.brightness,
                      }
                    : undefined,
            )
            this.playerController.enabledMouseAction(true)
            this.playerController.enabledKeyboardAction(true)
            // Landing in a room is landing on your feet -- an ember "restart" arrives this way
            // rather than as a respawn, and nothing else in a whole-room load would lift the
            // death freeze the run before left behind.
            this.playerController.setDowned(false)

            // The tilemap is here, which is NOT the same as the room being here: its entities are
            // still on the wire and their bodies still have spritesheets to fetch. Opening the
            // screen now shows the player bare ground and then half a second of pop-in, so hold the
            // black and let the frame loop decide when the scene is worth looking at (see
            // tickRoomReveal / RoomReveal).
            this.clientRenderer.gui().holdBlackout()
            this.roomReveal.arm(Date.now())
            // Nobody walks while the screen is shut. A teleport's fade-out already paralyzed them,
            // but only for its own second; this covers the whole hold, and the reveal lifts it.
            this.playerController.paralyze(true, RoomReveal.MAX_HOLD_MS)

            setTimeout(() => {
                this.playerController.perturb()
            }, 100)
        }

        if (leaderboards) {
            this.clientRenderer.gui().updateLeaderboard(leaderboards)
        }

        if (playerLocations) {
            this.clientRenderer.entity().updatePlayerLocations(playerLocations)
        }

        if (updatedRoom) {
            this.playerController.client.requestPlayerLocations()
            this.playerController.client.requestVisibility()
        }

        if (updatedQuadrant) {
            // alert("new quadrant")
        }

        return updatedRoom
    }

    updateFromPlayerMessage = (message: Message) => {
        if (this.playerEntity.entityId !== message.entityId) {
            return
        }

        if (message.roomId !== this.playerEntity.roomId) {
            this.playerEntity.roomId = message.roomId
        }

        this.playerEntity.campfireMenuLearned = message.campfireMenuLearned
        this.playerEntity.recruitMenuLearned = message.recruitMenuLearned
        this.playerEntity.mana = message.mana
        this.playerEntity.visible = message.visible
        this.playerEntity.canShootBullets = message.canShootBullets
        this.playerEntity.canLayBomb = message.canLayBomb
        // A fallen player who hasn't been stood back up yet doesn't get to walk around. Taken
        // from the server's verdict on every message rather than from a local timer, so it lifts
        // the instant the server lifts it -- and can't outlive it, which is what would strand a
        // player who was already alive again behind a controller that still thought otherwise.
        this.playerController.setDowned(!!message.downed)
        // ...and whether they are wearing one of another camp's attackers, which is what puts the
        // "Let go" button on the screen. Same shape as `downed` for the same reason: it is the
        // server's verdict on every message, so the only way out of a body cannot outlive it (or
        // go missing while it lives) because of a local timer disagreeing.
        this.clientRenderer.gui().setInvading(!!message.invading)
        // this.playerController.paralyze(message.paralyzeUntilTs > Date.now())
        this.playerEntity.upgradePoints = message.upgradePoints
        this.clientRenderer.entity().update(this.playerEntity)

        if (message.inventory) {
            this.updateInventoryFrom(this.playerEntity, message)
            this.playerController.restoreBuildingEquipment()
        }

        this.started = true
    }

    updateFromNonPlayerMessage = (message: Message) => {
        if (this.playerEntity.entityId === message.entityId) {
            return
        }

        if (message.roomId !== this.playerEntity.roomId) {
            return
        }

        const entity = this.entityManager.get(message.entityId)
        if (!entity) {
            return
        }

        if (message.inventory) {
            this.updateInventoryFrom(entity, message)
        }
    }

    updateInventoryFrom = (entity: Entity, message: Message) => {
        if (this.clientRenderer.gui().updateInventory(entity, message.inventory)) {
            entity.inventory = { ...message.inventory }
            return true
        }

        return false
    }

    scheduleForDestruction = (entityId: EntityId) => {
        this.destroyedEntities.add(entityId)
        this.destroyedEntitiesLagBuffer.add(entityId)
    }

    isDestroyed = (entityId: EntityId) => this.destroyedEntities.has(entityId)

    destroyEntity = (entity: Entity) => {
        if (entity) {
            this.entitySortCacheInvalidated = true
            this.clientRenderer.entity().destroy(entity)
        }
    }

    processEntities = (dt_sec: number) => {
        // process bullets
        this.entityManager.iterate("bullet", bullet => {
            if (bullet.isDead()) {
                this.scheduleForDestruction(bullet.entityId)
                return
            }

            const result = ApplyInputResultPool.instance.checkout()
            bullet.applyInput(
                undefined,
                dt_sec,
                bullet.speed2,
                result.data,
                collisionMover(this.tilemapMetaProvider),
                true,
            )
            if (!result.data?.movedX && !result.data?.movedY) {
                this.scheduleForDestruction(bullet.entityId)
            } else {
                const distance = bullet.travelledDistance()

                if (distance > bullet.maxDistance) {
                    this.scheduleForDestruction(bullet.entityId)
                }
            }
            ApplyInputResultPool.instance.release(result)
        })
    }

    cleanup = () => {
        const now = Date.now()
        if (now - this.expiredTs > 3000) {
            this.entityManager.iterate(
                null,
                next => {
                    this.scheduleForDestruction(next.entityId)
                },
                next => next.entityId !== this.playerEntity.entityId && next.roomId !== this.roomId,
            )
        }

        this.destroyedEntities.forEach(entityId => {
            const entity = this.entityManager.get(entityId)
            if (entity) {
                this.destroyEntity(entity)
                this.entityManager.remove(entity.entityId)
            }
        })
        this.destroyedEntities.clear()

        if (this.lagbufferTs.expired()) {
            this.destroyedEntitiesLagBuffer.clear()
        }
    }

    // Unified culling system methods
    setFocusEntityId = (entityId: EntityId | null) => {
        this.focusEntityId = entityId
    }

    // True if a follow-cam focus is active and the given location is within the focus render
    // radius of it — used to keep the focused entity's neighborhood rendering/interpolating even
    // when it is far from the player (so the PiP view is live).
    isNearFocus = (location: Coordinate): boolean => {
        if (!this.focusEntityId) {
            return false
        }
        const focus = this.entityManager.get(this.focusEntityId)
        if (!focus) {
            return false
        }
        return (
            getDistance(location.x, location.y, focus.location.x, focus.location.y) <
            ClientGameLogic.FOCUS_RENDER_RADIUS
        )
    }

    isTooDistantFromPlayer = (entity: Entity) => {
        return this.isTooDistantFromPlayerByLocation(entity.entityId, entity.location)
    }

    // Unified culling system methods
    isTooDistantFromPlayerByLocation = (entityId: EntityId, entityLocation: Coordinate) => {
        if (entityId === this.playerEntity.entityId) {
            return false // Never cull the player
        }
        return !this.isEntityWithinCullingDistance(entityId, entityLocation, this.playerEntity.location)
    }

    isPlayerInitialized = () => {
        return !(this.playerEntity.location?.x === -1 && this.playerEntity.location?.y === -1)
    }

    // Efficient culling check that combines distance and viewport-based culling
    isEntityWithinCullingDistance = (
        entityId: EntityId,
        entityLocation: Coordinate,
        playerPos: Coordinate,
    ): boolean => {
        if (entityId === this.playerEntity.entityId) {
            return true // Always include player
        }

        // Keep the follow-cam focus neighborhood live even when it is off the main viewport, so
        // the picture-in-picture view renders and interpolates it.
        if (this.isNearFocus(entityLocation)) {
            return true
        }

        // // First check: distance-based culling (existing)
        // const dx = entity.location.x - playerPos.x
        // const dy = entity.location.y - playerPos.y
        // const distSquared = dx * dx + dy * dy

        // if (distSquared > this.cullingDistanceSquared) {
        //     return false
        // }

        // Second check: viewport-based culling (new)
        const viewportBounds = this.getViewportBounds(playerPos)
        const entityX = entityLocation.x
        const entityY = entityLocation.y

        const isWithinViewport =
            entityX >= viewportBounds.minX &&
            entityX <= viewportBounds.maxX &&
            entityY >= viewportBounds.minY &&
            entityY <= viewportBounds.maxY

        return isWithinViewport
    }

    // Pure viewport test against the player's current position (no follow-cam focus exemption).
    // Used by the follow-cam PiP to hide itself once the watched entity is visible on the main screen.
    isWithinPlayerViewport = (location: Coordinate): boolean => {
        const bounds = this.getViewportBounds(this.playerEntity.location)
        return (
            location.x >= bounds.minX &&
            location.x <= bounds.maxX &&
            location.y >= bounds.minY &&
            location.y <= bounds.maxY
        )
    }

    // Get culling distance for other systems
    getCullingDistanceTiles = (): number => {
        return this.CULLING_DISTANCE_TILES
    }

    getCullingDistanceSquared = (): number => {
        return this.cullingDistanceSquared
    }

    // Check if entities need to be resorted for rendering optimization
    shouldResortEntities = (playerPos: Coordinate): boolean => {
        // Check if player moved significantly
        const playerMoved =
            !this.lastPlayerPosForViewport ||
            Math.abs(playerPos.x - this.lastPlayerPosForViewport.x) > 25 ||
            Math.abs(playerPos.y - this.lastPlayerPosForViewport.y) > 25

        // The renderer caches this viewport query too. Honor its invalidation when another
        // entity moves, appears or disappears, even while the player stands still.
        const viewportChanged = this.viewportManager?.needsVisibleEntitiesRefresh(playerPos) ?? false
        const playerInitialized = this.isPlayerInitialized()
        const shouldResort = !playerInitialized || playerMoved || this.entitySortCacheInvalidated || viewportChanged

        if (shouldResort) {
            this.lastPlayerPosForViewport = { x: playerPos.x, y: playerPos.y }
            this.entitySortCacheInvalidated = false
        }

        return shouldResort
    }

    // Invalidate viewport cache when needed (e.g., when map changes)
    invalidateViewportCache = (): void => {
        this.viewportBoundsValid = false
        // A camera update changes the query, not the indexed entity positions.
        // Room transitions reset the index separately when initializing the new map.
        this.viewportManager?.forceInvalidateCache()
    }

    // Invalidate entity sort cache when needed (e.g., when player position updates)
    invalidateEntitySortCache = (): void => {
        this.entitySortCacheInvalidated = true
    }

    // Optimized viewport bounds calculation with caching
    getViewportBounds = (playerPos: Coordinate): { minX: number; maxX: number; minY: number; maxY: number } => {
        // Check if we need to recalculate based on player movement
        const playerMoved =
            this.lastPlayerPosForViewport &&
            (Math.abs(playerPos.x - this.lastPlayerPosForViewport.x) > this.VIEWPORT_CACHE_THRESHOLD ||
                Math.abs(playerPos.y - this.lastPlayerPosForViewport.y) > this.VIEWPORT_CACHE_THRESHOLD)

        if (!this.viewportBoundsValid || playerMoved) {
            this.calculateViewportBounds(playerPos)
            this.lastPlayerPosForViewport = { x: playerPos.x, y: playerPos.y }
            this.viewportBoundsValid = true
        }

        return this.cachedViewportBounds
    }

    private calculateViewportBounds = (playerPos: Coordinate): void => {
        const X_FACTOR = ScalingParams.viewportX
        const Y_FACTOR = ScalingParams.viewportY - 1

        // Calculate viewport center based on TranslationComputer logic
        let viewportCenterX = playerPos.x
        let viewportCenterY = playerPos.y

        // Check if player is in bounds (not at edges)
        const inBoundsX =
            playerPos.x > TileSize * X_FACTOR && playerPos.x < (this.tilemapMetaProvider.width() - X_FACTOR) * TileSize
        const inBoundsY =
            playerPos.y > TileSize * Y_FACTOR && playerPos.y < (this.tilemapMetaProvider.height() - Y_FACTOR) * TileSize

        // If not in bounds, use edge positions (like TranslationComputer)
        if (!inBoundsX) {
            if (playerPos.x <= TileSize * X_FACTOR) {
                viewportCenterX = TileSize * X_FACTOR
            } else {
                viewportCenterX = (this.tilemapMetaProvider.width() - X_FACTOR) * TileSize
            }
        }

        if (!inBoundsY) {
            if (playerPos.y <= TileSize * Y_FACTOR) {
                viewportCenterY = TileSize * Y_FACTOR
            } else {
                viewportCenterY = (this.tilemapMetaProvider.height() - Y_FACTOR) * TileSize
            }
        }

        // Calculate viewport bounds
        const offsetWidth = X_FACTOR * TileSize + ScalingParams.viewportPaddingX
        const offsetHeight = Y_FACTOR * TileSize + ScalingParams.viewportPaddingY

        // Update the cached bounds object instead of creating a new one
        this.cachedViewportBounds.minX = viewportCenterX - offsetWidth
        this.cachedViewportBounds.maxX = viewportCenterX + offsetWidth
        this.cachedViewportBounds.minY = viewportCenterY - offsetHeight
        this.cachedViewportBounds.maxY = viewportCenterY + offsetHeight
    }

    doStruck = (entityId: EntityId, strikerEntityId: EntityId) => {
        const entity = this.entityManager.get(entityId)
        if (!entity) {
            return
        }

        if (!this.isTooDistantFromPlayer(entity)) {
            this.clientRenderer.entity().animateStrikeOn(entity)
            this.audio.hit(entity)
        }

        const strikerEntity = this.entityManager.get(strikerEntityId)
        if (strikerEntity) {
            this.scheduleForDestruction(strikerEntityId)
        }
        if (entity.entityId === this.playerEntity.entityId) {
            this.clientRenderer.map().screenShake(entity)
            if (entity.hp < Math.max(3, Math.ceil(entity.maxHp * 0.6))) {
                this.clientRenderer.gui().pulseDamage()
            }
        }
    }

    tileEffect = (effect: TileEffect) => {
        if (effect.hp !== undefined && effect.hpMax !== undefined && effect.effectType === "damage") {
            this.tilemapMetaProvider.updateMapDeltaWithHP(effect.mapRoughCoordinates, effect.hp, effect.hpMax)
        }

        this.clientRenderer.map().tileEffect(effect)
        if (!["tallGrass", "grassSprout", "tallReeds"].includes(effect.entityTile)) {
            this.audio.play("woodhit", {
                volume: 0.125,
                speed: 0.85 + Math.min(0.15, Math.random()),
            })
        }
    }

    detonate = (entityId: EntityId) => {
        const entity = this.entityManager.get(entityId)
        if (entity) {
            if (!this.isTooDistantFromPlayer(entity)) {
                this.clientRenderer.entity().detonate(entity)
            }
        }
    }

    attack = (entityId: EntityId, trigger?: Callback<void>) => {
        const entity = this.entityManager.get(entityId)
        if (entity) {
            if (!this.isTooDistantFromPlayer(entity)) {
                this.playerController.client.startAttack(entityId)
                this.clientRenderer.entity().attack(entity, trigger)
                this.audio.attack(entity)
            }
        }
    }

    updateActivation = (entityId: EntityId, active: boolean) => {
        const entity = this.entityManager.get(entityId)
        if (entity) {
            this.clientRenderer.entity().updateActivation(entity, active)
        }
    }

    itemPickup = (entity: Entity, itemTypeMeta: ItemTypeMeta, quantity: number) => {
        this.clientRenderer.entity().itemPickedUp(entity, itemTypeMeta, quantity)
        this.audio.pickup(itemTypeMeta)
    }

    orientation = (orientation: Orientation) => {
        this.playerController.updateOrientation(orientation)
    }

    emp = (entityId: EntityId, range: number) => {
        const entity = this.entityManager.get(entityId)
        if (entity) {
            this.clientRenderer.entity().emp(entity, range)
            this.audio.emp(entity)
        }
    }

    sparkle = (entityId: EntityId) => {
        const entity = this.entityManager.get(entityId)
        if (entity) {
            if (!this.isTooDistantFromPlayer(entity)) {
                this.clientRenderer.entity().sparkle(entity)
            }
        }
    }

    killed = (entityId: EntityId) => {
        const entity = this.entityManager.get(entityId)
        if (entityId === this.playerEntity.entityId) {
            this.clientRenderer.entity().death(this.playerEntity)

            this.playerController.paralyze(true, 5000)
            // this.clientRenderer.gui().fadeOut()
        } else {
            if (entity) {
                this.clientRenderer.entity().death(entity)
                this.audio.death(entity)
            }
        }
    }

    respawned = (entityId: EntityId) => {
        if (entityId === this.playerEntity.entityId) {
            // Alive again: lift the death freeze here rather than waiting for the next world-state
            // message to say so. The server cleared it before sending this, so the two agree --
            // this just spares the player a beat of being stood up but unable to move.
            this.playerController.setDowned(false)
            this.fadeInOut("in")
            setTimeout(() => {
                this.playerController.perturb()
            }, 100)
        }
    }

    teleported = (entityId: EntityId) => {
        if (entityId === this.playerEntity.entityId) {
            this.clientRenderer.map().reset()
        }
    }

    pleaseWait = () => {
        this.clientRenderer.gui().addScreenMessage({
            text: "Loading, please wait...",
            target: "important",
        })
        this.fadeInOut("inOut", 10000)
    }

    /**
     * One frame's worth of "is the room ready to be looked at yet?", driven from the end of the
     * frame loop so it reads the scene the player would have just been shown.
     *
     * Does nothing unless a room load is being held (see RoomReveal) -- which is the only state in
     * which the screen is deliberately black with nothing scheduled to lift it.
     */
    tickRoomReveal = () => {
        if (!this.roomReveal.isHolding()) {
            return
        }

        const ready = this.roomReveal.shouldReveal(Date.now(), {
            mapPending: this.clientRenderer.map().hasPendingRender(),
            spritesPending: this.clientRenderer.entity().pendingSpriteCount(),
        })

        if (ready) {
            this.fadeInOut("in")
        }
    }

    fadeInOut = (direction: "in" | "out" | "inOut", timeOut: number = 1000) => {
        if (direction === "in") {
            if (this.blackedOutTimeout) {
                clearTimeout(this.blackedOutTimeout)
                this.blackedOutTimeout = undefined
            }
            this.clientRenderer.gui().fadeIn()
            this.playerController.paralyze(false)
        }
        if (direction === "out") {
            this.clientRenderer.gui().fadeOut(true)
            this.playerController.paralyze(true, timeOut)
        }
        if (direction === "inOut") {
            this.fadeInOut("out", timeOut)
            this.blackedOutTimeout = setTimeout(() => {
                this.fadeInOut("in")
            }, timeOut)
        }
    }

    advanceEntity = (result: ApplyInputResult, entity: Entity, message: Message, allowSlide?: boolean) => {
        if (!this.tilemapMetaProvider) {
            return
        }

        return entity.applyInput(
            message.angle,
            message.press_time,
            message.speed2,
            result,
            collisionMover(this.tilemapMetaProvider),
            allowSlide,
            allowSlide,
        )
    }

    advancePlayer = (result: ApplyInputResult, message: Message) => {
        const { x: originalX, y: originalY } = this.playerEntity.location
        this.advanceEntity(result, this.playerEntity, message, true)
        this.clientRenderer.updatePlayerLocation(this.playerEntity)
        if (originalX !== this.playerEntity.location.x || originalY !== this.playerEntity.location.y) {
            this.audio.walk(this.playerEntity)
        }
    }

    // `hold` marks a line the player dismisses themselves: its bubble grows a "Next" button and
    // stays up until pressed, and anything the same entity says meanwhile queues behind it (see
    // AvatarHud.spoke). `holdId` is the token the speaker gave that line; pressing Next sends it
    // back so a script pacing a conversation off the player (ember-tutorial.js) knows WHICH line was
    // read and may speak the next one. The HUD-side copies below still expire on the usual clock --
    // they are captions for a speaker the player can't see, not the bubble itself.
    handleChat = (message: string, entityId: EntityId, entityName: string, hold?: boolean, holdId?: string) => {
        const entity = this.entityManager.get(entityId)

        // Player chat is server-wide by design, so the LOG takes every line it is given, wherever
        // the speaker is standing. Nothing SPATIAL does: a bubble, an offscreen arrow's caption, the
        // PiP caption -- those all claim the speaker is somewhere near, and for a speaker in another
        // room that is simply false.
        //
        // The server already keeps out-of-room lines off the wire (ServerGameLogic
        // .broadcastChatMessage), but a client can still be HOLDING a speaker it has just left
        // behind: entities are discarded lazily on a room change, so for a moment the camp the
        // player walked out of is still in the entity manager. A HELD line landing there never
        // fades -- it sits and everything after it queues up, which is the "the other camp's Lord is
        // collecting in mine" bug.
        const elsewhere = !!entity?.roomId && entity.roomId !== this.playerEntity?.roomId

        if (entityName) {
            this.clientRenderer.gui().appendChatHistory(message, entityName)
        }
        if (entity && !elsewhere) {
            this.clientRenderer.entity().spoke(entity, message, {
                hold,
                onDismissed: hold
                    ? () => this.playerController?.client?.sendSpeechDismissed(entityId, holdId)
                    : undefined,
            })
        }
        if (elsewhere) {
            return
        }

        // Record speech so an offscreen tracked member's bubble can ride its arrow.
        // Duration mirrors the avatar HUD bubble (avatar_hud.spokeImpl).
        const wordCount = message.split(" ").length
        const durationMs = Math.max(3000, ((wordCount * 60) / 200) * 1250)
        this.recentSpeech.set(entityId, { text: message, expiresAt: Date.now() + durationMs })
        // Longer-lived copy for the follow-cam PiP caption.
        this.lastSpeech.set(entityId, { text: message, expiresAt: Date.now() + 10000 })
    }

    // Returns the entity's current speech-bubble text, or null if none/expired.
    getRecentSpeech = (entityId: EntityId): string | null => {
        const s = this.recentSpeech.get(entityId)
        if (!s) {
            return null
        }
        if (Date.now() >= s.expiresAt) {
            this.recentSpeech.delete(entityId)
            return null
        }
        return s.text
    }

    // Returns the entity's last spoken line for the follow-cam PiP caption, or null once it's older
    // than ~10s. Outlives getRecentSpeech so the PiP keeps showing the last words a while.
    getLastSpeech = (entityId: EntityId): string | null => {
        const s = this.lastSpeech.get(entityId)
        if (!s) {
            return null
        }
        if (Date.now() >= s.expiresAt) {
            this.lastSpeech.delete(entityId)
            return null
        }
        return s.text
    }

    // Mirror the latest ember roster into the entityId -> role lookup used by the offscreen
    // faction tracker. Called alongside the HUD widget update from the roster broadcast.
    setEmberRoster = (roster: EmberRosterEntry[]) => {
        this.emberRoles.clear()
        for (const entry of roster) {
            this.emberRoles.set(entry.entityId, entry.role)
        }
    }

    getEmberRole = (entityId: EntityId): EmberRosterRole | null => this.emberRoles.get(entityId) ?? null

    hideRoomName = (hidden: boolean) => {
        this.clientRenderer.gui().hideRoomName(hidden)
    }

    updateLocation = (location: string, roomId?: string) => {
        if (!roomId || roomId === this.roomId) {
            this.clientRenderer.gui().updateLocation(location)
        }
    }

    renderScene = () => {
        // one ring to rule them all
        this.clientRenderer.render()
    }

    // Get shadow performance metrics for debugging
    getShadowPerformanceMetrics = () => {
        return this.clientRenderer.getShadowPerformanceMetrics()
    }

    // Force shadow texture update (useful for debugging)
    forceShadowTextureUpdate = () => {
        this.clientRenderer.forceShadowTextureUpdate()
    }

    createLocalEntity = (entitySrc: any) => {
        const entity = Entity.fromJson(entitySrc)
        setTimeout(() => {
            this.entityManager.set(entity)
            this.clientRenderer.entity().create(entity)
        }, 250)
    }

    destroyLocalEntity = (targetFlags: Record<string, any>) => {
        this.entityManager
            .collect(entity => {
                return !Object.keys(targetFlags).find(flag => targetFlags[flag] !== entity.flags[flag])
            })
            .forEach(entity => {
                entity.visible = false
                this.scheduleForDestruction(entity.entityId)
            })
    }

    playSoundEffect = (effect: string, loop: boolean, target: string) => {
        this.audio.soundEffect(effect, loop, target)
    }

    enablePlayerMouseAction = (enabled: boolean) => this.playerController?.enabledMouseAction(enabled)

    animateLocalEntityCollection = (entityId: EntityId, target: string, position?: number) => {
        const entity = this.entityManager.get(entityId)
        if (!entity) {
            return
        }
        entity.visible = false
        this.clientRenderer.gui().animateLocalEntityCollection(entity, target, position)
    }

    animateAbility = (abilityType: AbilityType, duration?: number) => {
        this.clientRenderer.gui().animateAbility(abilityType, duration)
    }

    upgradedAccount = () => {
        this.playerController.setIsGuest(false)
        this.clientRenderer.gui().helpMessaging()
    }

    updateDayPhase = (phase: DayPhase) => {
        if (phase?.name !== this.dayPhase?.name) {
            this.dayPhase = { ...phase }

            const targetBrightness = this.dayPhase.brightness
            if (this.brightnessInterval) {
                clearInterval(this.brightnessInterval)
            }
            this.brightnessInterval = setInterval(() => {
                if (this.currentBrightness > targetBrightness) {
                    this.currentBrightness -= 0.01
                } else if (this.currentBrightness < targetBrightness) {
                    this.currentBrightness += 0.01
                }

                if (Math.abs(this.currentBrightness - targetBrightness) <= 0.01) {
                    clearInterval(this.brightnessInterval)
                    this.currentBrightness = targetBrightness
                }

                const currentVisibility = this.clientRenderer.map().getVisibility()
                const visibility: Visibility = {
                    ...(currentVisibility || {}),
                    darkness: this.currentBrightness,
                }
                this.clientRenderer.map().update(null, visibility)
            }, 60)
        }
    }

    log = (text: string) => {
        this.playerController.client.logActivity(text)
    }

    incompleteEntity = (entity: Entity) => {
        this.incompleteEntities.add(entity.entityId)
    }

    nearbyIncompleteEntities = (): EntityId[] => {
        const entities = []
        this.incompleteEntities.forEach(e => {
            if (e === this.playerEntity.entityId) {
                return false
            }
            const inc = this.entityManager.get(e)
            if (!inc) {
                return false
            }
            const distance = getDistance(
                inc.location.x,
                inc.location.y,
                this.playerEntity.location.x,
                this.playerEntity.location.y,
            )
            if (distance < 10 * TileSize) {
                entities.push(e)
            }
        })

        return entities
    }

    clearIncompleteEntities = (entityIds: EntityId[]) => {
        entityIds.forEach(id => {
            this.incompleteEntities.delete(id)
        })
    }

    saveAppearance = (appearance: Appearance) => {
        this.playerController.client.saveAppearance(appearance)
    }

    invokeCrafting = () => {
        this.playerController.client.emitPlayerEvent({
            playerEventType: "REQUEST_CRAFTING",
            context: {},
        })
    }

    invokeInventory = (inventoryActionToken: string) => {
        this.playerController.client.emitPlayerEvent({
            playerEventType: "REQUEST_INVENTORY",
            context: {
                actionToken: inventoryActionToken,
            },
        })
    }

    invokeQuests = () => {
        this.playerController.client.emitPlayerEvent({
            playerEventType: "REQUEST_QUESTS",
            context: {},
        })
    }

    emitConversationResponse = (response: any) => {
        this.playerController.client.emitPlayerEvent({
            playerEventType: "CONVERSATION_RESPONSE",
            context: response,
        })
    }

    requestEntityInteractions = (entityId: EntityId) => {
        this.playerController.client.emitPlayerEvent({
            playerEventType: "REQUEST_ENTITY_INTERACTIONS",
            context: {
                entityId,
            },
        })
    }

    interactionCallback = (entityId: EntityId, interaction: InteractionType) => {
        // Clicking an interaction icon must never also trigger a world attack. The click that
        // fires attacks originates from a document-level mousedown that doesn't know a pixi
        // interaction widget was hit, so briefly suppress attacks around the activation.
        this.playerController.suppressAttack()
        this.playerController.client.emitPlayerEvent({
            playerEventType: "ACTIVATE_ENTITY_INTERACTION",
            context: {
                interaction,
                entityId,
            },
        })
    }

    selectSlot = (itemId: string) => {
        const item = this.playerEntity.inventory?.items.find(i => i.id === itemId)
        if (!item) {
            return
        }
        this.playerController.client.inventoryAction(
            this.playerEntity.entityId,
            itemId,
            item.active ? "deactivate" : "activate",
        )
    }

    // Back out of an armed placement (the "Ready" option on a buildable item, or a build the player
    // no longer wants to commit). Deactivating the item is what actually ends build mode: the server's
    // building atom turns the terrain pointer back off when it sees INACTIVATE_ITEM.
    cancelBuildPlacement = () => {
        const item = this.playerEntity?.inventory?.items?.find(
            i => i.active && Mechanics.item.properties.buildable(i.itemTypeMeta),
        )
        if (!item) {
            return
        }
        this.playerController.client.inventoryAction(this.playerEntity.entityId, item.id, "deactivate")
    }

    activateWorldPoint = (roomFinePoint: Coordinate, options?: BuildingPointOptions) => {
        if (emptyCoordinate(roomFinePoint)) {
            return
        }
        this.playerController.client.activateWorldPoint(roomFinePoint, options)
    }

    entityClicked = (entity: Entity) => {
        if (entity.clickable) {
            // todo - rename this, entity.clickable means that the server has some
            // plan for whenever the player explicitly clicks on the entity
            this.playerController.client.entityClicked(entity)
        }
    }

    // Spatial indexing methods for efficient culling
    private initializeViewportManager = (): void => {
        if (!this.tilemapMetaProvider) {
            return
        }

        // Get world dimensions
        const worldWidth = this.tilemapMetaProvider.width() * TileSize
        const worldHeight = this.tilemapMetaProvider.height() * TileSize

        // Reuse existing viewport manager if it exists, otherwise create new one
        if (this.viewportManager) {
            // Reset with new dimensions
            this.viewportManager.reset(worldWidth, worldHeight)
        } else {
            // Create new viewport manager
            this.viewportManager = new ClientViewportManager(this.entityManager, worldWidth, worldHeight)
        }
    }

    // Apply a server-sent faction-tracking config (e.g. on entering an ember room).
    setFactionTracking = (config: FactionTrackingConfig) => {
        this.trackedFaction = config.faction
        this.trackingEnabled = config.enabled && !!config.faction
    }

    // Apply a server-sent room ambience (sent on every room entry, empty included).
    setRoomAmbience = (ambience: RoomAmbience) => {
        this.ambience = ambience
    }

    // Get visible entities using spatial indexing - O(log n) instead of O(n)
    getVisibleEntities = (playerPos: Coordinate): Array<{ entity: Entity; distSquared: number }> => {
        if (!this.viewportManager) {
            // Fallback to old method if viewport manager not initialized
            return this.getVisibleEntitiesFallback(playerPos)
        }

        return this.viewportManager.getVisibleEntities(playerPos)
    }

    // Fallback method using the old O(n) approach
    private getVisibleEntitiesFallback = (playerPos: Coordinate): Array<{ entity: Entity; distSquared: number }> => {
        const allEntities = this.entityManager.collect(() => true)
        const entitiesWithDistance: Array<{ entity: Entity; distSquared: number }> = []

        for (const entity of allEntities) {
            if (this.isEntityWithinCullingDistance(entity.entityId, entity.location, playerPos)) {
                const dx = entity.location.x - playerPos.x
                const dy = entity.location.y - playerPos.y
                const distSquared = dx * dx + dy * dy
                entitiesWithDistance.push({ entity, distSquared })
            }
        }

        entitiesWithDistance.sort((a, b) => a.distSquared - b.distSquared)
        return entitiesWithDistance
    }

    // Get performance statistics for the viewport manager
    getViewportManagerStats = () => {
        return this.viewportManager?.getPerformanceStats() || null
    }

    // Force cache invalidation (useful for debugging)
    forceInvalidateViewportCache = (): void => {
        this.viewportManager?.forceInvalidateCache()
    }
}
